// scheduling/pages/AdminConsole.jsx
// Admin page with sub-tabs: Recommendation Config + Sync & Data dashboard.
// Committee-only access (isSuperUser).

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { fetchRecConfig, fetchSyncSummary, fetchHolidays, fetchGlobalAssignments, fetchPTO, fetchStaff, fetchCustomJobs, fetchCalendars, fetchCapacityDrift } from '../services/schedData';
import { updateRecConfig, saveHoliday, deleteHoliday, saveGlobalAssignment, deleteGlobalAssignment, approvePTO, rejectPTO, upsertCustomJob, deleteCustomJob, upsertCalendar, deleteCalendar, acceptPECapacity } from '../services/schedApi';
import { BRAND, STAFF_DEPT_GROUPS, normalizeDept, CUSTOM_JOB_CATEGORY_COLORS, CUSTOM_JOB_CATEGORIES } from '../utils/schedConstants';
import { useUrlState } from '../../hooks/useUrlState';
import BugReportsTab from './BugReportsTab';
import SuggestionsTab from './SuggestionsTab';
import PoolAuditTab from './PoolAuditTab';
import ClosedJobsTab from './ClosedJobsTab';

// Effective-range label for a calendar template. Recurring templates show month/day
// only (year ignored — they repeat every year); absolute ones show full ISO dates.
function fmtEffective(row) {
  if (!row.EffectiveStart && !row.EffectiveEnd) return 'Year-round';
  const fmt = (v) => {
    if (!v) return '—';
    const iso = String(v).slice(0, 10);
    if (!row.RecurringAnnually) return iso;
    const [y, m, d] = iso.split('-');
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  return `${fmt(row.EffectiveStart)} ${row.RecurringAnnually ? '–' : '→'} ${fmt(row.EffectiveEnd)}`;
}

// ── Recommendation Config constants ─────────────────────────────────────────
const WEIGHT_KEYS = ['weight_experience', 'weight_interest', 'weight_team_composition'];
const WEIGHT_LABELS = {
  weight_experience:       'Experience',
  weight_interest:         'Niche Interest',
  weight_team_composition: 'Team Composition',
};
const WEIGHT_DESCS = {
  weight_experience:       'How much historical WIP hours on this client/niche/service line influence the score.',
  weight_interest:         "How much the staff member's ranked niche preferences influence the score.",
  weight_team_composition: 'How much Working Genius team balance influences the score (only applied when job flag is set).',
};
const THRESHOLD_KEYS = ['min_pop_size', 'min_hours_threshold', 'experience_lookback_years'];
const THRESHOLD_LABELS = {
  min_pop_size: 'Min Population Size',
  min_hours_threshold: 'Min Hours Threshold',
  experience_lookback_years: 'Experience Lookback (years)',
};
// Per-key minimum for the numeric input. Most thresholds floor at 1; the
// experience lookback allows 0, which means "all-time, no limit".
const THRESHOLD_MIN = { experience_lookback_years: 0 };
const THRESHOLD_DESCS = {
  min_pop_size:        'Minimum number of staff with meaningful hours on a client/niche before local PERCENT_RANK is used. Below this, global rank is used instead. Takes effect on next PE sync.',
  min_hours_threshold: 'Minimum hours a staff member must have in a dimension to be included in the ranking pool. Staff below this get Level 0. Takes effect on next PE sync.',
  experience_lookback_years: 'How many years of historical WIP hours feed the experience scores. 0 = all-time (no limit). Changing this re-scores everyone on the next PE sync.',
};

function fmtTimestamp(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function AdminConsole() {
  const navigate = useNavigate();
  const { isSuperUser, isDevSuperUser, ready, username } = useAuth();
  // Admin sub-tab lives in the URL (?tab=) for shareable / navbot deeplinks.
  const [adminTab, setAdminTab] = useUrlState('tab', 'config', {
    validate: ['config', 'pto_requests', 'holidays', 'global_assignments', 'global_templates', 'custom_jobs', 'pool_audit', 'closed_jobs', 'sync', 'bug_reports', 'suggestions'],
  });

  // ── Recommendation Config state ──
  const [weights, setWeights] = useState({ weight_experience: 60, weight_interest: 25, weight_team_composition: 15 });
  const [thresholds, setThresholds] = useState({ min_pop_size: 5, min_hours_threshold: 10, experience_lookback_years: 0 });
  const [descriptions, setDescriptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [error, setError] = useState(null);

  // ── Holidays state ──
  const [holidayList, setHolidayList] = useState([]);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ name: '', holidayType: 'date', month: 1, day: 1, patternRule: 'FIRST_MON', patternMonth: 1, capacityHours: 0 });
  const [holidaySaving, setHolidaySaving] = useState(false);

  const PATTERN_OPTIONS = [
    { value: 'FIRST_MON', label: 'First Monday' },
    { value: 'SECOND_MON', label: 'Second Monday' },
    { value: 'THIRD_MON', label: 'Third Monday' },
    { value: 'LAST_MON', label: 'Last Monday' },
    { value: 'FOURTH_THU', label: 'Fourth Thursday' },
    { value: 'DAY_AFTER_FOURTH_THU', label: 'Day after Fourth Thursday' },
    { value: 'MON_BEFORE_FOURTH_THU', label: 'Mon before Fourth Thursday (Thanksgiving wk)' },
    { value: 'TUE_BEFORE_FOURTH_THU', label: 'Tue before Fourth Thursday (Thanksgiving wk)' },
    { value: 'WED_BEFORE_FOURTH_THU', label: 'Wed before Fourth Thursday (Thanksgiving wk)' },
  ];

  const COMMON_US_HOLIDAYS = [
    { name: "New Year's Day", holidayType: 'date', month: 1, day: 1 },
    { name: 'Martin Luther King Jr. Day', holidayType: 'pattern', patternRule: 'THIRD_MON', patternMonth: 1 },
    { name: "Presidents' Day", holidayType: 'pattern', patternRule: 'THIRD_MON', patternMonth: 2 },
    { name: 'Memorial Day', holidayType: 'pattern', patternRule: 'LAST_MON', patternMonth: 5 },
    { name: 'Juneteenth', holidayType: 'date', month: 6, day: 19 },
    { name: 'Independence Day', holidayType: 'date', month: 7, day: 4 },
    { name: 'Labor Day', holidayType: 'pattern', patternRule: 'FIRST_MON', patternMonth: 9 },
    { name: 'Columbus Day', holidayType: 'pattern', patternRule: 'SECOND_MON', patternMonth: 10 },
    { name: 'Veterans Day', holidayType: 'date', month: 11, day: 11 },
    { name: 'Thanksgiving Week (Monday)', holidayType: 'pattern', patternRule: 'MON_BEFORE_FOURTH_THU', patternMonth: 11 },
    { name: 'Thanksgiving Week (Tuesday)', holidayType: 'pattern', patternRule: 'TUE_BEFORE_FOURTH_THU', patternMonth: 11 },
    { name: 'Thanksgiving Week (Wednesday)', holidayType: 'pattern', patternRule: 'WED_BEFORE_FOURTH_THU', patternMonth: 11 },
    { name: 'Thanksgiving', holidayType: 'pattern', patternRule: 'FOURTH_THU', patternMonth: 11 },
    { name: 'Day after Thanksgiving', holidayType: 'pattern', patternRule: 'DAY_AFTER_FOURTH_THU', patternMonth: 11 },
    { name: 'Christmas', holidayType: 'date', month: 12, day: 25 },
  ];

  const loadHolidays = useCallback(() => {
    setHolidayLoading(true);
    fetchHolidays().then(d => setHolidayList(d || [])).catch(() => {}).finally(() => setHolidayLoading(false));
  }, []);

  useEffect(() => { if (isSuperUser && adminTab === 'holidays') loadHolidays(); }, [isSuperUser, adminTab, loadHolidays]);


  const handleSaveHoliday = async () => {
    if (!holidayForm.name.trim()) return;
    setHolidaySaving(true);
    try {
      await saveHoliday({ ...holidayForm, createdBy: username });
      setHolidayForm({ name: '', holidayType: 'date', month: 1, day: 1, patternRule: 'FIRST_MON', patternMonth: 1, capacityHours: 0 });
      loadHolidays();
    } catch (e) { alert('Save failed: ' + e.message); }
    setHolidaySaving(false);
  };

  const handleDeleteHoliday = async (id) => {
    try { await deleteHoliday(id); loadHolidays(); } catch (e) { alert('Delete failed: ' + e.message); }
  };

  const handleSeedHolidays = async () => {
    setHolidaySaving(true);
    try {
      for (const h of COMMON_US_HOLIDAYS) {
        if (holidayList.some(ex => ex.Name === h.name)) continue; // skip dupes
        await saveHoliday({ ...h, capacityHours: 0, createdBy: username });
      }
      loadHolidays();
    } catch (e) { alert('Seed failed: ' + e.message); }
    setHolidaySaving(false);
  };

  // Helper: compute observed date for preview
  const getObservedPreview = (form) => {
    const year = new Date().getFullYear();
    let date;
    if (form.holidayType === 'date') {
      date = new Date(year, (form.month || 1) - 1, form.day || 1);
      const origDay = date.getDay();
      if (origDay === 6) date.setDate(date.getDate() - 1);
      if (origDay === 0) date.setDate(date.getDate() + 1);
      const adjusted = origDay === 6 || origDay === 0;
      return { date, adjusted, origDow: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][origDay] };
    } else {
      // Pattern — compute for current year
      const m = (form.patternMonth || 1) - 1;
      const rule = form.patternRule || 'FIRST_MON';
      if (rule === 'LAST_MON') { date = new Date(year, m + 1, 0); while (date.getDay() !== 1) date.setDate(date.getDate() - 1); }
      else if (rule === 'FIRST_MON') { date = new Date(year, m, 1); while (date.getDay() !== 1) date.setDate(date.getDate() + 1); }
      else if (rule === 'SECOND_MON') { let c=0; date = new Date(year, m, 1); while (c<2) { if (date.getDay()===1) c++; if (c<2) date.setDate(date.getDate()+1); } }
      else if (rule === 'THIRD_MON') { let c=0; date = new Date(year, m, 1); while (c<3) { if (date.getDay()===1) c++; if (c<3) date.setDate(date.getDate()+1); } }
      else if (rule === 'FOURTH_THU') { let c=0; date = new Date(year, m, 1); while (c<4) { if (date.getDay()===4) c++; if (c<4) date.setDate(date.getDate()+1); } }
      else if (rule === 'DAY_AFTER_FOURTH_THU') { let c=0; date = new Date(year, m, 1); while (c<4) { if (date.getDay()===4) c++; if (c<4) date.setDate(date.getDate()+1); } date.setDate(date.getDate()+1); }
      else if (rule === 'MON_BEFORE_FOURTH_THU' || rule === 'TUE_BEFORE_FOURTH_THU' || rule === 'WED_BEFORE_FOURTH_THU') { let c=0; date = new Date(year, m, 1); while (c<4) { if (date.getDay()===4) c++; if (c<4) date.setDate(date.getDate()+1); } date.setDate(date.getDate() - (rule === 'MON_BEFORE_FOURTH_THU' ? 3 : rule === 'TUE_BEFORE_FOURTH_THU' ? 2 : 1)); }
      else date = new Date(year, m, 1);
      return { date, adjusted: false };
    }
  };

  // ── Global Assignments state ──
  const [gaList, setGaList] = useState([]);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaForm, setGaForm] = useState({ name: '', assignmentDate: '', hours: 4, description: '', departments: null });
  const [gaSaving, setGaSaving] = useState(false);
  const [gaSelectedDepts, setGaSelectedDepts] = useState(new Set()); // empty = all

  const loadGlobalAssignments = useCallback(() => {
    setGaLoading(true);
    fetchGlobalAssignments().then(d => setGaList(d || [])).catch(() => {}).finally(() => setGaLoading(false));
  }, []);

  useEffect(() => { if (isSuperUser && adminTab === 'global_assignments') loadGlobalAssignments(); }, [isSuperUser, adminTab, loadGlobalAssignments]);

  const handleSaveGA = async () => {
    if (!gaForm.name.trim() || !gaForm.assignmentDate) return;
    setGaSaving(true);
    try {
      await saveGlobalAssignment({
        ...gaForm,
        departments: gaSelectedDepts.size > 0 ? [...gaSelectedDepts] : null,
        createdBy: username,
      });
      setGaForm({ name: '', assignmentDate: '', hours: 4, description: '', departments: null });
      setGaSelectedDepts(new Set());
      loadGlobalAssignments();
    } catch (e) { alert('Save failed: ' + e.message); }
    setGaSaving(false);
  };

  const handleDeleteGA = async (id) => {
    try { await deleteGlobalAssignment(id); loadGlobalAssignments(); } catch (e) { alert('Delete failed: ' + e.message); }
  };

  // ── Custom Jobs state (Item 2) ──
  const [cjList, setCjList] = useState([]);
  const [cjLoading, setCjLoading] = useState(false);
  const [cjForm, setCjForm] = useState({ name: '', description: '', category: 'Other' });
  const [cjSaving, setCjSaving] = useState(false);
  const [cjEditingId, setCjEditingId] = useState(null);
  const [cjEditForm, setCjEditForm] = useState({ name: '', description: '', category: 'Other' });
  const [cjShowInactive, setCjShowInactive] = useState(false);

  const loadCustomJobs = useCallback(() => {
    setCjLoading(true);
    fetchCustomJobs().then(d => setCjList(d || [])).catch(() => {}).finally(() => setCjLoading(false));
  }, []);

  useEffect(() => { if (isSuperUser && adminTab === 'custom_jobs') loadCustomJobs(); }, [isSuperUser, adminTab, loadCustomJobs]);

  const handleSaveCJ = async () => {
    if (!cjForm.name.trim()) return;
    setCjSaving(true);
    try {
      await upsertCustomJob({
        name: cjForm.name.trim(),
        description: cjForm.description.trim() || null,
        category: cjForm.category,
        isActive: true,
        updatedBy: username,
      });
      setCjForm({ name: '', description: '', category: 'Other' });
      loadCustomJobs();
    } catch (e) { alert('Save failed: ' + e.message); }
    setCjSaving(false);
  };

  const handleStartEditCJ = (row) => {
    setCjEditingId(row.CustomJobId);
    setCjEditForm({ name: row.Name, description: row.Description || '', category: row.Category });
  };

  const handleSaveEditCJ = async () => {
    if (!cjEditForm.name.trim()) return;
    setCjSaving(true);
    try {
      await upsertCustomJob({
        customJobId: cjEditingId,
        name: cjEditForm.name.trim(),
        description: cjEditForm.description.trim() || null,
        category: cjEditForm.category,
        isActive: true,
        updatedBy: username,
      });
      setCjEditingId(null);
      loadCustomJobs();
    } catch (e) { alert('Save failed: ' + e.message); }
    setCjSaving(false);
  };

  const handleToggleActiveCJ = async (row) => {
    try {
      await upsertCustomJob({
        customJobId: row.CustomJobId,
        name: row.Name,
        description: row.Description,
        category: row.Category,
        isActive: !row.IsActive,
        updatedBy: username,
      });
      loadCustomJobs();
    } catch (e) { alert('Toggle failed: ' + e.message); }
  };

  const handleDeleteCJ = async (row) => {
    // Soft-delete by default; if no scheduled entries reference it, allow hard-delete.
    if (!window.confirm(`Deactivate "${row.Name}"? Existing scheduled hours will remain but no one will be able to schedule against it.`)) return;
    try { await deleteCustomJob(row.CustomJobId, { updatedBy: username }); loadCustomJobs(); }
    catch (e) { alert('Delete failed: ' + e.message); }
  };

  // ── Global Capacity Templates state (Item 9) ──
  // Admin-only editor for the FIRM-WIDE global templates (StaffId IS NULL).
  // Per-staff overrides are NOT managed here — those still live on the
  // employee profile capacity tab (admin must navigate there to add/edit them).
  const emptyGT = { templateId: undefined, staffId: null, templateName: '', departmentCode: '',
                    effectiveStart: '', effectiveEnd: '', recurringAnnually: true,
                    monHours: 8, tueHours: 8, wedHours: 8, thuHours: 8, friHours: 8, satHours: 0, sunHours: 0,
                    isActive: true, priority: 0 };
  // Fresh form for a given view — per-staff custom templates default to NOT recurring
  // (they're usually one-off / open-ended), globals default to recurring (busy season).
  const blankGT = (view) => ({ ...emptyGT, recurringAnnually: view === 'global' });
  const [gtView, setGtView] = useState('global'); // 'global' | 'staff'
  const [gtList, setGtList] = useState([]);        // global templates (StaffId null)
  const [gtStaffList, setGtStaffList] = useState([]); // per-staff custom templates
  const [gtStaff, setGtStaff] = useState([]);      // active staff (for picker + name resolution)
  const [gtLoading, setGtLoading] = useState(false);
  const [gtForm, setGtForm] = useState(emptyGT);
  const [gtSaving, setGtSaving] = useState(false);
  const [gtEditingId, setGtEditingId] = useState(null);
  const [gtShowInactive, setGtShowInactive] = useState(false);
  const [gtDeleteConfirm, setGtDeleteConfirm] = useState(null); // row pending delete-confirm, or null
  const [gtDeleteBusy, setGtDeleteBusy] = useState(false);
  const [gtStaffQuery, setGtStaffQuery] = useState('');     // staff typeahead text (staff-custom form)
  const [gtStaffMenuOpen, setGtStaffMenuOpen] = useState(false);

  const loadTemplates = useCallback(() => {
    setGtLoading(true);
    // fetchCalendars() with no staffId returns globals + every per-staff override.
    // Split into the two lists; fetch staff for the picker + name resolution.
    Promise.all([fetchCalendars(), fetchStaff()])
      .then(([cals, staff]) => {
        const all = cals || [];
        setGtList(all.filter(t => t.StaffId == null));
        setGtStaffList(all.filter(t => t.StaffId != null));
        setGtStaff((staff || []).filter(s => s.IsActive !== false && s.IsActive !== 0));
      })
      .catch(() => {})
      .finally(() => setGtLoading(false));
  }, []);

  useEffect(() => { if (isSuperUser && adminTab === 'global_templates') loadTemplates(); }, [isSuperUser, adminTab, loadTemplates]);

  // Switch between Global and Staff-custom views — reset the editor for the new view.
  const switchGtView = (view) => {
    setGtView(view);
    setGtEditingId(null);
    setGtForm(blankGT(view));
    setGtStaffQuery('');
    setGtStaffMenuOpen(false);
  };
  const gtStaffName = useCallback((id) => gtStaff.find(s => s.StaffId === id)?.Name || `Staff #${id}`, [gtStaff]);

  const handleSaveGT = async () => {
    if (!gtForm.templateName.trim()) return;
    if (gtView === 'staff' && !gtForm.staffId) { alert('Pick a staff member for this custom template.'); return; }
    setGtSaving(true);
    try {
      await upsertCalendar({
        templateId: gtForm.templateId || undefined,
        staffId: gtView === 'staff' ? gtForm.staffId : null,
        departmentCode: gtForm.departmentCode || null,
        templateName: gtForm.templateName.trim(),
        effectiveStart: gtForm.effectiveStart || null,
        effectiveEnd: gtForm.effectiveEnd || null,
        recurringAnnually: !!gtForm.recurringAnnually,
        monHours: Number(gtForm.monHours) || 0,
        tueHours: Number(gtForm.tueHours) || 0,
        wedHours: Number(gtForm.wedHours) || 0,
        thuHours: Number(gtForm.thuHours) || 0,
        friHours: Number(gtForm.friHours) || 0,
        satHours: Number(gtForm.satHours) || 0,
        sunHours: Number(gtForm.sunHours) || 0,
        isActive: gtForm.isActive !== false,
        priority: Number(gtForm.priority) || 0,
        createdBy: username,
      });
      setGtForm(blankGT(gtView));
      setGtEditingId(null);
      setGtStaffQuery('');
      loadTemplates();
    } catch (e) { alert('Save failed: ' + e.message); }
    setGtSaving(false);
  };

  const gtFormRef = useRef(null);
  const handleEditGT = (row) => {
    setGtEditingId(row.TemplateId);
    setGtForm({
      templateId: row.TemplateId,
      staffId: row.StaffId || null,
      templateName: row.TemplateName || '',
      departmentCode: row.DepartmentCode || '',
      effectiveStart: row.EffectiveStart ? String(row.EffectiveStart).slice(0, 10) : '',
      effectiveEnd: row.EffectiveEnd ? String(row.EffectiveEnd).slice(0, 10) : '',
      recurringAnnually: !!row.RecurringAnnually,
      monHours: row.MonHours, tueHours: row.TueHours, wedHours: row.WedHours,
      thuHours: row.ThuHours, friHours: row.FriHours,
      satHours: row.SatHours, sunHours: row.SunHours,
      isActive: row.IsActive !== false,
      priority: row.Priority || 0,
    });
    setGtStaffQuery(row.StaffId ? gtStaffName(row.StaffId) : '');
    setGtStaffMenuOpen(false);
    // Scroll the form into view so the user can immediately see what they're editing.
    setTimeout(() => gtFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // Open the confirmation modal — actual delete happens after user confirms.
  const handleDeleteGT = (row) => setGtDeleteConfirm(row);

  const confirmDeleteGT = async () => {
    if (!gtDeleteConfirm) return;
    setGtDeleteBusy(true);
    try {
      await deleteCalendar(gtDeleteConfirm.TemplateId);
      // If the row being deleted was also the one being edited, clear the editor.
      if (gtEditingId === gtDeleteConfirm.TemplateId) { setGtEditingId(null); setGtForm(blankGT(gtView)); }
      setGtDeleteConfirm(null);
      loadTemplates();
    } catch (e) { alert('Delete failed: ' + e.message); }
    setGtDeleteBusy(false);
  };

  // ── PTO Requests state ──
  const [ptoList, setPtoList] = useState([]);
  const [ptoStaff, setPtoStaff] = useState([]);
  const [ptoLoading, setPtoLoading] = useState(false);
  const [ptoStatusFilter, setPtoStatusFilter] = useState('pending');
  const [ptoSearch, setPtoSearch] = useState('');
  const [ptoDeptFilter, setPtoDeptFilter] = useState('all'); // 'all' or a STAFF_DEPT_GROUPS id

  const loadPtoRequests = useCallback(() => {
    setPtoLoading(true);
    Promise.all([
      fetchPTO({ startDate: new Date().toISOString().slice(0, 10) }),
      fetchStaff(),
    ]).then(([pto, staff]) => {
      setPtoList(pto || []);
      setPtoStaff(staff || []);
    }).catch(() => {}).finally(() => setPtoLoading(false));
  }, []);

  useEffect(() => { if (isSuperUser && adminTab === 'pto_requests') loadPtoRequests(); }, [isSuperUser, adminTab, loadPtoRequests]);

  // Group PTO day-rows into requests by LeaveReqID, filtered by status + search.
  // Department is applied afterwards so the dropdown can show per-department counts.
  const ptoBase = (() => {
    const staffMap = {};
    for (const s of ptoStaff) staffMap[s.StaffId] = s;
    const byReq = {};
    for (const row of ptoList) {
      const key = row.LeaveReqID || `${row.StaffId}_${row.PTODate}`;
      if (!byReq[key]) {
        byReq[key] = {
          leaveReqId: row.LeaveReqID,
          staffId: row.StaffId,
          staffName: staffMap[row.StaffId]?.Name || `Staff #${row.StaffId}`,
          department: staffMap[row.StaffId]?.Department || '',
          departmentCode: staffMap[row.StaffId]?.DepartmentCode || '',
          status: row.Status || 'approved',
          requestNotes: row.RequestNotes,
          requestedOn: row.RequestedOn,
          decisionBy: row.DecisionBy,
          leaveType: row.LeaveType,
          totalHours: 0,
          days: [],
        };
      }
      byReq[key].totalHours += (row.Hours || 0);
      const dayISO = (typeof row.PTODate === 'string' ? row.PTODate : '').slice(0, 10);
      byReq[key].days.push({ date: dayISO, hours: row.Hours });
    }
    // Derive date range from actual expanded days (not PE metadata, which includes weekends)
    let list = Object.values(byReq).map(r => {
      const sorted = r.days.map(d => d.date).filter(Boolean).sort();
      r.startDate = sorted[0] || null;
      r.endDate = sorted[sorted.length - 1] || null;
      return r;
    });
    // Filter by status
    if (ptoStatusFilter !== 'all') list = list.filter(r => r.status === ptoStatusFilter);
    // Search by staff name
    if (ptoSearch.trim()) {
      const q = ptoSearch.toLowerCase();
      list = list.filter(r => r.staffName.toLowerCase().includes(q));
    }
    // Sort: pending first, then by start date
    list.sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (b.status === 'pending' && a.status !== 'pending') return 1;
      return (a.startDate || '').localeCompare(b.startDate || '');
    });
    return list;
  })();

  // Department dropdown: STAFF_DEPT_GROUPS that have requests under the current status + search.
  // The selected group stays listed even at 0 so the <select> never loses its value.
  const ptoDeptCounts = {};
  for (const r of ptoBase) {
    const g = normalizeDept(r.departmentCode);
    ptoDeptCounts[g] = (ptoDeptCounts[g] || 0) + 1;
  }
  const ptoDeptOptions = STAFF_DEPT_GROUPS.filter(g => ptoDeptCounts[g.id] || g.id === ptoDeptFilter);
  const ptoRequests = ptoDeptFilter === 'all'
    ? ptoBase
    : ptoBase.filter(r => normalizeDept(r.departmentCode) === ptoDeptFilter);

  const [ptoActioning, setPtoActioning] = useState(null); // leaveReqId being actioned

  const handlePtoAction = async (leaveReqId, action) => {
    const label = action === 'approve' ? 'approve' : 'reject';
    if (!window.confirm(`Are you sure you want to ${label} this PTO request?`)) return;
    setPtoActioning(leaveReqId);
    try {
      if (action === 'approve') await approvePTO(leaveReqId);
      else await rejectPTO(leaveReqId);
      loadPtoRequests(); // refresh list
    } catch (e) {
      alert(`Failed to ${label}: ${e.message}`);
    }
    setPtoActioning(null);
  };

  // ── Sync & Data state ──
  const [syncSummary, setSyncSummary] = useState(null);
  // Capacity drift vs PE (Trello #332)
  const [drift, setDrift] = useState(null);
  const [driftLoading, setDriftLoading] = useState(false);
  const [driftErr, setDriftErr] = useState(null);
  const [driftMsg, setDriftMsg] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  // Load recommendation config
  useEffect(() => {
    // Dev-only users (DEV_SUPERUSER but not Production Committee) reach this page
    // for the Bug Reports tab only — clear the config-load gate so they aren't
    // stuck on "Loading…" (the rest of the page is hidden from them anyway).
    if (!isSuperUser) { setLoading(false); return; }
    fetchRecConfig()
      .then(({ config, rows }) => {
        const w = { ...weights };
        const t = { ...thresholds };
        const d = {};
        for (const row of rows) {
          d[row.ConfigKey] = row.Description;
          if (WEIGHT_KEYS.includes(row.ConfigKey)) w[row.ConfigKey] = parseInt(row.ConfigValue, 10) || 0;
          if (THRESHOLD_KEYS.includes(row.ConfigKey)) t[row.ConfigKey] = parseInt(row.ConfigValue, 10) || 0;
        }
        setWeights(w); setThresholds(t); setDescriptions(d);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [isSuperUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dev-only users (not Production Committee) can only use the Bug Reports tab —
  // pin the URL/active tab there so the lone visible tab reads as selected.
  useEffect(() => {
    if (ready && isDevSuperUser && !isSuperUser && adminTab !== 'bug_reports' && adminTab !== 'suggestions') setAdminTab('bug_reports');
  }, [ready, isDevSuperUser, isSuperUser, adminTab, setAdminTab]);

  // Load sync summary when Sync tab is active
  useEffect(() => {
    if (adminTab !== 'sync' || !isSuperUser) return;
    setSyncLoading(true);
    fetchSyncSummary()
      .then(data => setSyncSummary(data))
      .catch(() => {})
      .finally(() => setSyncLoading(false));
  }, [adminTab, isSuperUser]);

  // ── Capacity drift vs PE (Trello #332) ──
  const loadDrift = useCallback(() => {
    setDriftLoading(true); setDriftErr(null);
    return fetchCapacityDrift()
      .then(d => setDrift(d))
      .catch(e => setDriftErr(e.message))
      .finally(() => setDriftLoading(false));
  }, []);

  useEffect(() => {
    if (adminTab !== 'sync' || !isSuperUser) return;
    loadDrift();
  }, [adminTab, isSuperUser, loadDrift]);

  // Adopt PE's work profile for one staffer. Per-row, never bulk: a mis-resolved bulk
  // apply would rewrite every staffer's capacity at once.
  const handleAcceptPE = useCallback(async (row) => {
    const stepLines = row.steps.map(s => {
      const what = s.action === 'repoint-default' ? `point default at ${s.templateId}:${s.templateName}`
        : s.action === 'deactivate-override' ? `deactivate ${s.templateId}:${s.templateName}`
        : `${s.templateId ? 'update' : 'create'} per-staff override ${s.hours.join('/')}`;
      return `  • ${what}`;
    }).join('\n');
    const ok = window.confirm(
      `Adopt Practice Engine's capacity for ${row.name}?\n\n`
      + `RPRS now : ${(row.rprsHours || []).join('/')}  (${row.rprsWeek}h/wk)\n`
      + `PE says  : ${(row.peHours || []).join('/')}  (${row.peWeek}h/wk)\n\n`
      + `${row.steps.length} change${row.steps.length === 1 ? '' : 's'}:\n${stepLines}\n\n`
      + `This changes ${row.name}'s scheduling capacity in RPRS only — it does not write to PE.`
    );
    if (!ok) return;
    setAcceptingId(row.staffId); setDriftErr(null);
    try {
      const res = await acceptPECapacity(row.staffId);
      setDriftMsg(`${res.staffName}: ${res.previousWeek}h → ${res.peWeek}h (${res.applied.length} change${res.applied.length === 1 ? '' : 's'} applied)`);
      await loadDrift();
      setTimeout(() => setDriftMsg(null), 12000);
    } catch (e) {
      setDriftErr(`${row.name}: ${e.message}`);
    } finally {
      setAcceptingId(null);
    }
  }, [loadDrift]);

  // ── Sync handlers ──
  const handlePESync = useCallback(async () => {
    setSyncing(true); setSyncMsg('Syncing PE data...');
    try {
      const targets = ['staff', 'clients', 'jobs', 'jobs_history', 'experience', 'overbudget'];
      const results = {};
      for (const t of targets) {
        setSyncMsg(`Syncing PE: ${t}...`);
        const res = await fetch(`/api/peSync?target=${t}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
        const data = await res.json();
        results[t] = data.count ?? data.inserted ?? '?';
      }
      setSyncMsg(`PE sync done! ${Object.entries(results).map(([k, v]) => `${k}: ${v}`).join(', ')}`);
      // Refresh summary
      fetchSyncSummary().then(d => setSyncSummary(d)).catch(() => {});
    } catch (e) { setSyncMsg('PE sync error: ' + e.message); }
    finally { setSyncing(false); setTimeout(() => setSyncMsg(null), 15000); }
  }, []);

  // handleEmpireSync / handleEmpireUpload lived here and were removed with their
  // buttons — see the Sync Actions block. They posted to api/empireSync, which
  // imports AND deletes rprs.ScheduleEntries.

  if (!isSuperUser && !isDevSuperUser) {
    return (
      <div className="sched-placeholder">
        <h2>Access Restricted</h2>
        <p>This page is for Production Committee members only.</p>
      </div>
    );
  }

  const weightTotal = WEIGHT_KEYS.reduce((s, k) => s + (weights[k] || 0), 0);
  const weightError = weightTotal !== 100;

  function setWeight(key, val) { setWeights(prev => ({ ...prev, [key]: Math.max(0, Math.min(100, val)) })); }
  function setThreshold(key, val) { setThresholds(prev => ({ ...prev, [key]: Math.max(1, val) })); }

  async function handleSave() {
    if (weightError) return;
    setSaving(true); setSaveMsg(null);
    try {
      const updates = [
        ...WEIGHT_KEYS.map(k => ({ configKey: k, configValue: String(weights[k]) })),
        ...THRESHOLD_KEYS.map(k => ({ configKey: k, configValue: String(thresholds[k]) })),
      ];
      await updateRecConfig(updates, username);
      setSaveMsg('Saved. Weight changes apply immediately. Threshold changes apply on next PE sync.');
      setTimeout(() => setSaveMsg(null), 8000);
    } catch (e) { setSaveMsg('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading...</div></div>;
  if (error) return <div className="sched-placeholder"><h2>Error</h2><p style={{ color: '#DC2626' }}>{error}</p></div>;

  return (
    <div className="sched-emp-root">
      <div className="sched-emp-header">
        <button className="sched-sug-back" onClick={() => navigate(-1)}>&larr; Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: BRAND.deepTeal }}>Admin Console</div>
        </div>
      </div>

      {/* Sub-tab navigation — matches Dashboard tab styling. Production-Committee
          tabs show for super-users; Bug Reports shows for DEV_SUPERUSER devs;
          Suggestions shows for either (super-user OR dev). */}
      <div className="sched-tabs">
        {[
          ...(isSuperUser ? [
            { key: 'config', label: 'Recommendation Config' },
            { key: 'pto_requests', label: 'PTO Requests' },
            { key: 'holidays', label: 'Holidays' },
            { key: 'global_assignments', label: 'Global Assignments' },
            { key: 'global_templates', label: 'Capacity Templates' },
            { key: 'custom_jobs', label: 'Custom Jobs' },
            { key: 'pool_audit', label: 'Pool Audit' },
            { key: 'closed_jobs', label: 'Closed Job Cleanup' },
            { key: 'sync', label: 'Sync & Data' },
          ] : []),
          ...(isDevSuperUser ? [{ key: 'bug_reports', label: 'Bug Reports' }] : []),
          ...((isSuperUser || isDevSuperUser) ? [{ key: 'suggestions', label: 'Suggestions' }] : []),
        ].map(tab => (
          <button key={tab.key}
            className={`sched-tab ${adminTab === tab.key ? 'sched-tab--active' : ''}`}
            onClick={() => setAdminTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 40px' }}>
        {/* Capacity Templates + Bug Reports + Suggestions need a wider canvas
            (7-day grid / triage table). Other tabs stay at the 680 form width. */}
        <div style={{ maxWidth: (adminTab === 'global_templates' || adminTab === 'bug_reports' || adminTab === 'suggestions' || adminTab === 'pool_audit') ? 1200 : 680, margin: '0 auto' }}>

        {/* Dev-only users (not Production Committee) only ever see the dev tabs —
            short-circuit so no super-only tab content can render for them. */}
        {(!isSuperUser && isDevSuperUser) ? (
          adminTab === 'suggestions' ? <SuggestionsTab /> : <BugReportsTab />
        ) : (
        <>

        {/* ════ Recommendation Config Tab ════ */}
        {adminTab === 'config' && (
          <>
            {/* Scoring Weights */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>Scoring Weights</h3>
                <span style={{
                  fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
                  background: weightError ? '#FEE2E2' : '#D1FAE5', color: weightError ? '#DC2626' : '#065F46',
                }}>
                  Total: {weightTotal} / 100{weightError ? ' \u26A0 must equal 100' : ''}
                </span>
              </div>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 4, marginBottom: 20 }}>
                Weights determine how each factor contributes to the overall candidate fit score (0{'\u2013'}100). They must sum to exactly 100.
              </p>
              {WEIGHT_KEYS.map(key => (
                <div key={key} style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{WEIGHT_LABELS[key]}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="number" min={0} max={100} step={5} value={weights[key]}
                        onChange={e => setWeight(key, parseInt(e.target.value, 10) || 0)}
                        style={{ width: 56, padding: '4px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, textAlign: 'center' }} />
                      <span style={{ fontSize: 12, color: '#9CA3AF', width: 20 }}>%</span>
                    </div>
                  </div>
                  <input type="range" min={0} max={100} step={5} value={weights[key]}
                    onChange={e => setWeight(key, parseInt(e.target.value, 10))}
                    style={{ width: '100%', accentColor: BRAND.deepTeal }} />
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{descriptions[key] || WEIGHT_DESCS[key]}</div>
                </div>
              ))}
            </div>

            {/* Scoring Thresholds */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>Scoring Thresholds</h3>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 4, marginBottom: 20 }}>
                These control how the experience scoring bell curve is calculated. Changes take effect on the <strong>next PE sync</strong>.
              </p>
              {THRESHOLD_KEYS.map(key => {
                const minVal = THRESHOLD_MIN[key] ?? 1;
                return (
                <div key={key} style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{THRESHOLD_LABELS[key]}</span>
                    <input type="number" min={minVal} step={1} value={thresholds[key]}
                      onChange={e => { const v = parseInt(e.target.value, 10); setThreshold(key, Number.isFinite(v) && v >= minVal ? v : minVal); }}
                      style={{ width: 72, padding: '4px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, textAlign: 'center' }} />
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>{descriptions[key] || THRESHOLD_DESCS[key]}</div>
                </div>
                );
              })}
            </div>

            {/* Save */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="sched-assign-btn" onClick={handleSave} disabled={saving || weightError}
                style={{ opacity: weightError ? 0.5 : 1 }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              {saveMsg && <span style={{ fontSize: 12, color: saveMsg.startsWith('Error') ? '#DC2626' : '#065F46' }}>{saveMsg}</span>}
            </div>
          </>
        )}

        {/* ════ PTO Requests Tab ════ */}
        {adminTab === 'pto_requests' && (
          <>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>PTO Requests</h3>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, lineHeight: 1.5 }}>
                View and manage PTO requests synced from PE. Pending requests are awaiting approval.
              </div>
            </div>

            {/* Filters row */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
              {/* Status pills */}
              <div style={{ display: 'flex', gap: 4 }}>
                {[
                  { key: 'all', label: 'All' },
                  { key: 'pending', label: 'Pending' },
                  { key: 'approved', label: 'Approved' },
                  { key: 'rejected', label: 'Rejected' },
                ].map(f => (
                  <button key={f.key} onClick={() => setPtoStatusFilter(f.key)}
                    style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, borderRadius: 6, border: 'none', cursor: 'pointer',
                      background: ptoStatusFilter === f.key ? (f.key === 'pending' ? '#3B82F6' : BRAND.deepTeal) : '#F3F4F6',
                      color: ptoStatusFilter === f.key ? '#fff' : '#6B7280', transition: 'all 0.15s' }}>
                    {f.label}
                  </button>
                ))}
              </div>
              {/* Department filter */}
              <select value={ptoDeptFilter} onChange={e => setPtoDeptFilter(e.target.value)} aria-label="Filter by department"
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, background: '#fff', cursor: 'pointer' }}>
                <option value="all">All departments ({ptoBase.length})</option>
                {ptoDeptOptions.map(g => (
                  <option key={g.id} value={g.id}>{g.label} ({ptoDeptCounts[g.id] || 0})</option>
                ))}
              </select>
              {/* Staff search */}
              <input type="text" placeholder="Search staff..." value={ptoSearch} onChange={e => setPtoSearch(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, width: 180 }} />
              {/* Count */}
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginLeft: 'auto' }}>
                {ptoRequests.length} request{ptoRequests.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Request list */}
            {ptoLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Loading...</div>
            ) : ptoRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>{'\uD83C\uDFD6\uFE0F'}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#9CA3AF' }}>
                  {ptoStatusFilter === 'pending' ? 'No pending PTO requests' : 'No PTO requests found'}
                </div>
                <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 4 }}>
                  {ptoDeptFilter !== 'all' ? 'Try another department or adjust your filters.'
                    : ptoStatusFilter === 'pending' ? 'All requests have been reviewed.' : 'Try adjusting your filters.'}
                </div>
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Staff</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Dates</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Hours</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Days</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Status</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Requested</th>
                      <th style={{ padding: '8px 4px', width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ptoRequests.map(req => {
                      const startD = req.startDate ? new Date((typeof req.startDate === 'string' ? req.startDate : '').slice(0, 10) + 'T12:00:00') : null;
                      const endD = req.endDate ? new Date((typeof req.endDate === 'string' ? req.endDate : '').slice(0, 10) + 'T12:00:00') : null;
                      const dateRange = startD && endD && startD.getTime() === endD.getTime()
                        ? startD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        : `${startD ? startD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?'} \u2013 ${endD ? endD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?'}`;
                      const requestedDate = req.requestedOn ? new Date(req.requestedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '\u2014';
                      const isPending = req.status === 'pending';
                      return (
                        <tr key={req.leaveReqId || `${req.staffId}_${req.startDate}`} style={{ borderTop: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ fontWeight: 600, color: '#111827' }}>{req.staffName}</div>
                            {req.department && <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1 }}>{req.department}</div>}
                          </td>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{dateRange}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: isPending ? '#3B82F6' : '#6B7280' }}>{req.totalHours}h</td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6B7280' }}>{req.days.length}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                              background: isPending ? '#DBEAFE' : req.status === 'rejected' ? '#FEE2E2' : '#D1FAE5',
                              color: isPending ? '#1D4ED8' : req.status === 'rejected' ? '#991B1B' : '#065F46' }}>
                              {isPending ? 'PENDING' : req.status === 'rejected' ? 'REJECTED' : 'APPROVED'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ fontSize: 11, color: '#6B7280' }}>{requestedDate}</div>
                            {req.decisionBy && <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1 }}>by {req.decisionBy}</div>}
                            {req.requestNotes && !['New Leave Request', 'Leave Request'].includes(req.requestNotes) && (
                              <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2, fontStyle: 'italic' }}>
                                {req.requestNotes.replace(/^New Leave Request\s*[-–]\s*/i, '')}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              <button onClick={() => {
                                  const cells = req.days.map(d => `${req.staffId}_${d.date}`);
                                  sessionStorage.setItem('gridViewContext', JSON.stringify({
                                    tab: 'calendar',
                                    deptFilter: req.departmentCode || 'all',
                                    scrollToStaffId: req.staffId,
                                    newCells: cells,
                                    message: `PTO: ${req.staffName} ${dateRange}`,
                                  }));
                                  navigate('/schedule/grid');
                                }}
                                style={{ background: '#F3F4F6', border: 'none', borderRadius: 4, cursor: 'pointer', padding: '4px 8px',
                                  fontSize: 10, fontWeight: 700, color: '#6B7280', transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#3B82F6'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#6B7280'; }}
                                title="View in grid">
                                View
                              </button>
                            {isPending && req.leaveReqId && (
                              <>
                                <button onClick={() => handlePtoAction(req.leaveReqId, 'approve')}
                                  disabled={ptoActioning === req.leaveReqId}
                                  style={{ background: '#D1FAE5', border: 'none', borderRadius: 4, cursor: 'pointer', padding: '4px 8px',
                                    fontSize: 11, fontWeight: 700, color: '#065F46', transition: 'all 0.15s' }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#10B981'; e.currentTarget.style.color = '#fff'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#D1FAE5'; e.currentTarget.style.color = '#065F46'; }}
                                  title="Approve request">
                                  {ptoActioning === req.leaveReqId ? '...' : '\u2713'}
                                </button>
                                <button onClick={() => handlePtoAction(req.leaveReqId, 'reject')}
                                  disabled={ptoActioning === req.leaveReqId}
                                  style={{ background: '#FEE2E2', border: 'none', borderRadius: 4, cursor: 'pointer', padding: '4px 8px',
                                    fontSize: 11, fontWeight: 700, color: '#991B1B', transition: 'all 0.15s' }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#EF4444'; e.currentTarget.style.color = '#fff'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#FEE2E2'; e.currentTarget.style.color = '#991B1B'; }}
                                  title="Reject request">
                                  {ptoActioning === req.leaveReqId ? '...' : '\u2717'}
                                </button>
                              </>
                            )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ════ Holidays Tab ════ */}
        {adminTab === 'holidays' && (
          <>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>Holidays</h3>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, lineHeight: 1.5 }}>
                  Manage firm-wide holidays. Holidays reduce daily capacity for all staff.<br />
                  <span style={{ color: '#9CA3AF' }}>Fixed dates auto-adjust for weekends (Sat &rarr; Fri, Sun &rarr; Mon).</span>
                </div>
              </div>
              {holidayList.length === 0 && (
                <button className="sched-assign-btn" onClick={handleSeedHolidays} disabled={holidaySaving}
                  style={{ fontSize: 12, padding: '8px 16px', whiteSpace: 'nowrap' }}>
                  {holidaySaving ? 'Adding...' : '+ Add Common US Holidays'}
                </button>
              )}
            </div>

            {/* Add holiday form */}
            <div style={{ background: '#F9FAFB', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.deepTeal, marginBottom: 14 }}>+ Add Holiday</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'end' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  {/* Name */}
                  <div style={{ flex: '1 1 180px' }}>
                    <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Name</label>
                    <input type="text" value={holidayForm.name} onChange={e => setHolidayForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g. Memorial Day" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  {/* Type */}
                  <div>
                    <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Type</label>
                    <div style={{ display: 'flex', gap: 2, background: '#E5E7EB', borderRadius: 6, padding: 2 }}>
                      <button type="button" onClick={() => setHolidayForm(p => ({ ...p, holidayType: 'date' }))}
                        style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, borderRadius: 5, border: 'none', cursor: 'pointer',
                          background: holidayForm.holidayType === 'date' ? BRAND.deepTeal : 'transparent', color: holidayForm.holidayType === 'date' ? '#fff' : '#6B7280' }}>
                        Fixed Date
                      </button>
                      <button type="button" onClick={() => setHolidayForm(p => ({ ...p, holidayType: 'pattern' }))}
                        style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, borderRadius: 5, border: 'none', cursor: 'pointer',
                          background: holidayForm.holidayType === 'pattern' ? BRAND.deepTeal : 'transparent', color: holidayForm.holidayType === 'pattern' ? '#fff' : '#6B7280' }}>
                        Pattern
                      </button>
                    </div>
                  </div>
                  {/* Date or pattern fields */}
                  {holidayForm.holidayType === 'date' ? (
                    <>
                      <div>
                        <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Month</label>
                        <select value={holidayForm.month} onChange={e => setHolidayForm(p => ({ ...p, month: parseInt(e.target.value) }))}
                          style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}>
                          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Day</label>
                        <input type="number" min="1" max="31" value={holidayForm.day}
                          onChange={e => setHolidayForm(p => ({ ...p, day: parseInt(e.target.value) || 1 }))}
                          style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, width: 55 }} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Rule</label>
                        <select value={holidayForm.patternRule} onChange={e => setHolidayForm(p => ({ ...p, patternRule: e.target.value }))}
                          style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}>
                          {PATTERN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>of Month</label>
                        <select value={holidayForm.patternMonth} onChange={e => setHolidayForm(p => ({ ...p, patternMonth: parseInt(e.target.value) }))}
                          style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}>
                          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  {/* Capacity */}
                  <div>
                    <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Capacity</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="number" min="0" max="8" step="0.5" value={holidayForm.capacityHours}
                        onChange={e => setHolidayForm(p => ({ ...p, capacityHours: parseFloat(e.target.value) || 0 }))}
                        style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, width: 55 }} />
                      <span style={{ fontSize: 11, color: '#9CA3AF' }}>hrs</span>
                    </div>
                  </div>
                </div>
                <button className="sched-assign-btn" style={{ padding: '8px 20px', fontSize: 13, whiteSpace: 'nowrap' }}
                  disabled={!holidayForm.name.trim() || holidaySaving} onClick={handleSaveHoliday}>
                  {holidaySaving ? 'Saving...' : 'Add Holiday'}
                </button>
              </div>
              {/* Live preview */}
              {holidayForm.name && (() => {
                const preview = getObservedPreview(holidayForm);
                const dateStr = preview.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                return (
                  <div style={{ marginTop: 12, padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, color: '#374151' }}>
                    <span style={{ color: '#9CA3AF' }}>{new Date().getFullYear()} observed date:</span>{' '}
                    <strong>{dateStr}</strong>
                    {preview.adjusted && <span style={{ color: '#D97706', fontWeight: 600 }}> &mdash; adjusted from {preview.origDow}</span>}
                  </div>
                );
              })()}
            </div>

            {/* Holiday list */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
              Configured Holidays ({holidayList.length})
            </div>
            {holidayLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Loading holidays...</div>
            ) : holidayList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>&#127876;</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#9CA3AF' }}>No holidays configured</div>
                <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 4 }}>Use the form above or seed common US holidays to get started.</div>
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Holiday</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Rule</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>{new Date().getFullYear()} Date</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Cap</th>
                      <th style={{ padding: '8px 4px', width: 30 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {holidayList.map(h => {
                      const preview = getObservedPreview(h.HolidayType === 'date'
                        ? { holidayType: 'date', month: h.Month, day: h.Day }
                        : { holidayType: 'pattern', patternRule: h.PatternRule, patternMonth: h.PatternMonth });
                      const dateStr = preview.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                      const MONTHS = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      return (
                        <tr key={h.HolidayId} style={{ borderTop: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#7C3AED', flexShrink: 0 }} />
                              <span style={{ fontWeight: 600, color: '#111827' }}>{h.Name}</span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px', color: '#6B7280' }}>
                            {h.HolidayType === 'date'
                              ? `${MONTHS[h.Month]} ${h.Day}`
                              : `${(PATTERN_OPTIONS.find(o => o.value === h.PatternRule) || {}).label || h.PatternRule} of ${MONTHS[h.PatternMonth]}`}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#374151', fontWeight: 600 }}>
                            {dateStr}
                            {preview.adjusted && <span style={{ color: '#D97706', fontWeight: 400, marginLeft: 4, fontSize: 10 }}>adjusted</span>}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: h.CapacityHours > 0 ? '#D97706' : '#059669', fontWeight: 600 }}>
                            {h.CapacityHours > 0 ? `${h.CapacityHours}h` : 'Off'}
                          </td>
                          <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                            <button onClick={() => handleDeleteHoliday(h.HolidayId)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#D1D5DB', padding: 0, lineHeight: 1, transition: 'color 0.15s' }}
                              onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
                              onMouseLeave={e => e.currentTarget.style.color = '#D1D5DB'}
                              title="Delete holiday">&times;</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ════ Global Assignments Tab ════ */}
        {adminTab === 'global_assignments' && (
          <>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>Global Assignments</h3>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, lineHeight: 1.5 }}>
                Firm-wide events that block time for staff. These appear as overlays on the scheduler grid and reduce available capacity.
              </div>
            </div>

            {/* Add form */}
            <div style={{ background: '#F9FAFB', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.deepTeal, marginBottom: 14 }}>+ Add Global Assignment</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Name</label>
                  <input type="text" value={gaForm.name} onChange={e => setGaForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Risk Seminar" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Date</label>
                  <input type="date" value={gaForm.assignmentDate} onChange={e => setGaForm(p => ({ ...p, assignmentDate: e.target.value }))}
                    style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Hours</label>
                  <input type="number" min="0.5" max="8" step="0.5" value={gaForm.hours}
                    onChange={e => setGaForm(p => ({ ...p, hours: parseFloat(e.target.value) || 0 }))}
                    style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, width: 60 }} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Description (optional)</label>
                  <input type="text" value={gaForm.description} onChange={e => setGaForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              {/* Department selector */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.3 }}>Departments</label>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setGaSelectedDepts(new Set())}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, borderRadius: 5, border: 'none', cursor: 'pointer',
                      background: gaSelectedDepts.size === 0 ? BRAND.deepTeal : '#F3F4F6', color: gaSelectedDepts.size === 0 ? '#fff' : '#6B7280' }}>
                    All
                  </button>
                  {STAFF_DEPT_GROUPS.filter(d => d.id !== 'STEERING').map(dept => (
                    <button key={dept.id} type="button"
                      onClick={() => setGaSelectedDepts(prev => {
                        const next = new Set(prev);
                        next.has(dept.id) ? next.delete(dept.id) : next.add(dept.id);
                        return next;
                      })}
                      style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 5, border: 'none', cursor: 'pointer',
                        background: gaSelectedDepts.has(dept.id) ? dept.color : '#F3F4F6', color: gaSelectedDepts.has(dept.id) ? '#fff' : '#6B7280' }}>
                      {dept.label.split(' ')[0]}
                    </button>
                  ))}
                </div>
              </div>
              <button className="sched-assign-btn" style={{ padding: '8px 20px', fontSize: 13 }}
                disabled={!gaForm.name.trim() || !gaForm.assignmentDate || gaSaving} onClick={handleSaveGA}>
                {gaSaving ? 'Saving...' : 'Add Assignment'}
              </button>
            </div>

            {/* Assignment list */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
              Scheduled Events ({gaList.length})
            </div>
            {gaLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Loading...</div>
            ) : gaList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>&#128197;</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#9CA3AF' }}>No global assignments configured</div>
                <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 4 }}>Use the form above to add firm-wide events.</div>
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Date</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Event</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Hours</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Depts</th>
                      <th style={{ padding: '8px 4px', width: 30 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaList.map(ga => {
                      const d = ga.AssignmentDate ? new Date(typeof ga.AssignmentDate === 'string' ? ga.AssignmentDate.slice(0, 10) + 'T12:00:00' : ga.AssignmentDate) : null;
                      const dateStr = d ? d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                      const isPast = d && d < new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00');
                      return (
                        <tr key={ga.AssignmentId} style={{ borderTop: '1px solid #F3F4F6', opacity: isPast ? 0.5 : 1 }}>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#0891B2', flexShrink: 0 }} />
                              {dateStr}
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ fontWeight: 600, color: '#111827' }}>{ga.Name}</div>
                            {ga.Description && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{ga.Description}</div>}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#0891B2' }}>{ga.Hours}h</td>
                          <td style={{ padding: '10px 12px', color: '#6B7280', fontSize: 11 }}>
                            {ga.Departments ? ga.Departments.split(',').map(dept => (
                              <span key={dept} style={{ display: 'inline-block', padding: '1px 5px', background: '#F3F4F6', borderRadius: 3, marginRight: 3, fontSize: 10 }}>{dept}</span>
                            )) : <span style={{ color: '#059669', fontWeight: 600 }}>All</span>}
                          </td>
                          <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                            <button onClick={() => handleDeleteGA(ga.AssignmentId)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#D1D5DB', padding: 0, lineHeight: 1, transition: 'color 0.15s' }}
                              onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
                              onMouseLeave={e => e.currentTarget.style.color = '#D1D5DB'}
                              title="Delete assignment">&times;</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ════ Custom Jobs Tab (Item 2) ════ */}
        {adminTab === 'custom_jobs' && (
          <>
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>Custom Jobs</h3>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6B7280' }}>
                Firm-internal categories that staff can be scheduled against (e.g., EBP Team, CPE, Recruiting).
                Distinct from real client jobs &mdash; no client, budget, or PE backing.
              </p>
              {/* Category is not just a colour: the Gen Track balance report reads it to
                  decide whether these hours count as tax or assurance work. Without this
                  note an admin has no way to know that picking 'Other' quietly removes
                  the hours from that report. */}
              <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
                <strong>Category</strong> sets the block colour on the grid <em>and</em> how the hours are
                counted in <strong>Reporting &rarr; Gen Track Balance</strong>:{' '}
                <span style={{ background: CUSTOM_JOB_CATEGORY_COLORS.Tax.bg, color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3 }}>Tax</span>{' '}and{' '}
                <span style={{ background: CUSTOM_JOB_CATEGORY_COLORS.Assurance.bg, color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3 }}>Assurance</span>{' '}
                count toward a staffer&rsquo;s balance; <strong>Administrative</strong> and <strong>Other</strong> do not.
              </p>

              {/* Add new */}
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>Add Custom Job</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Name</label>
                    <input type="text" value={cjForm.name}
                      onChange={e => setCjForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g., EBP Team"
                      style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Category</label>
                    <select value={cjForm.category}
                      onChange={e => setCjForm(p => ({ ...p, category: e.target.value }))}
                      style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                      {CUSTOM_JOB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Description (optional)</label>
                  <input type="text" value={cjForm.description}
                    onChange={e => setCjForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Short description shown on hover"
                    style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <button className="sched-assign-btn"
                  disabled={!cjForm.name.trim() || cjSaving}
                  onClick={handleSaveCJ}
                  style={{ fontSize: 12 }}>
                  {cjSaving ? 'Saving...' : 'Add Custom Job'}
                </button>
              </div>

              {/* List */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Active list
                </div>
                <label style={{ fontSize: 11, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 'auto' }}>
                  <input type="checkbox" checked={cjShowInactive} onChange={e => setCjShowInactive(e.target.checked)} />
                  Show deactivated
                </label>
              </div>
              {cjLoading ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading...</div>
              ) : cjList.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                  No custom jobs yet. Add one above to get started.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #E5E7EB' }}>Name</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #E5E7EB' }}>Category</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #E5E7EB' }}>Description</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #E5E7EB' }}>Last Updated</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #E5E7EB', width: 160 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cjList
                      .filter(r => cjShowInactive || r.IsActive)
                      .map(row => {
                        const isEditing = cjEditingId === row.CustomJobId;
                        const catColor = CUSTOM_JOB_CATEGORY_COLORS[row.Category] || { bg: '#6B7280', text: '#fff' };
                        return (
                          <tr key={row.CustomJobId} style={{ borderBottom: '1px solid #F3F4F6', opacity: row.IsActive ? 1 : 0.5 }}>
                            {isEditing ? (
                              <>
                                <td style={{ padding: '6px 10px' }}>
                                  <input type="text" value={cjEditForm.name}
                                    onChange={e => setCjEditForm(p => ({ ...p, name: e.target.value }))}
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 12 }} />
                                </td>
                                <td style={{ padding: '6px 10px' }}>
                                  <select value={cjEditForm.category}
                                    onChange={e => setCjEditForm(p => ({ ...p, category: e.target.value }))}
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 12, background: '#fff' }}>
                                    {CUSTOM_JOB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                </td>
                                <td style={{ padding: '6px 10px' }}>
                                  <input type="text" value={cjEditForm.description}
                                    onChange={e => setCjEditForm(p => ({ ...p, description: e.target.value }))}
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 12 }} />
                                </td>
                                <td style={{ padding: '6px 10px', color: '#9CA3AF', fontSize: 11 }}>—</td>
                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                                  <button onClick={handleSaveEditCJ} disabled={cjSaving}
                                    style={{ background: BRAND.deepTeal, color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginRight: 6 }}>
                                    Save
                                  </button>
                                  <button onClick={() => setCjEditingId(null)} disabled={cjSaving}
                                    style={{ background: 'none', border: '1px solid #D1D5DB', color: '#374151', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>
                                    Cancel
                                  </button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{row.Name}</td>
                                <td style={{ padding: '8px 10px' }}>
                                  <span style={{ background: catColor.bg, color: catColor.text, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, letterSpacing: 0.3 }}>
                                    {row.Category}
                                  </span>
                                </td>
                                <td style={{ padding: '8px 10px', color: '#6B7280' }}>{row.Description || <span style={{ color: '#D1D5DB' }}>—</span>}</td>
                                <td style={{ padding: '8px 10px', color: '#6B7280', fontSize: 11 }}>
                                  {row.UpdatedAt ? `${fmtTimestamp(row.UpdatedAt)}${row.UpdatedBy ? ` by ${row.UpdatedBy}` : ''}` : '—'}
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  <button onClick={() => handleStartEditCJ(row)}
                                    style={{ background: 'none', border: '1px solid #D1D5DB', color: '#374151', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer', marginRight: 6 }}>
                                    Edit
                                  </button>
                                  {row.IsActive ? (
                                    <button onClick={() => handleDeleteCJ(row)}
                                      style={{ background: 'none', border: '1px solid #FECACA', color: '#991B1B', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                      Deactivate
                                    </button>
                                  ) : (
                                    <button onClick={() => handleToggleActiveCJ(row)}
                                      style={{ background: 'none', border: '1px solid #BBF7D0', color: '#065F46', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                      Reactivate
                                    </button>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* ════ Global Capacity Templates Tab (Item 9) ════ */}
        {adminTab === 'global_templates' && (
          <>
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>Capacity Templates</h3>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7280' }}>
                Firmwide <strong>global</strong> templates any staffer can be linked to (Full Time, PT 30, busy-season overrides),
                plus per-staff <strong>custom</strong> calendars. Per-staff overrides take precedence over globals.
              </p>
              <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                {[['global', 'Global'], ['staff', 'Staff custom']].map(([key, label]) => (
                  <button key={key}
                    className={`sched-mgr-pill${gtView === key ? ' sched-mgr-pill--active' : ''}`}
                    onClick={() => switchGtView(key)}>{label}</button>
                ))}
              </div>

              {/* Add / edit form */}
              <div ref={gtFormRef}
                style={{ background: gtEditingId ? '#FFFBEB' : '#F9FAFB', border: `1px solid ${gtEditingId ? '#FDE68A' : '#E5E7EB'}`, borderRadius: 8, padding: 16, marginBottom: 16, transition: 'background 0.15s, border-color 0.15s' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: gtEditingId ? '#92400E' : '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {gtEditingId ? `Editing: ${gtForm.templateName || 'Template'}` : (gtView === 'staff' ? 'Add Staff-Custom Template' : 'Add Global Template')}
                </div>
                {gtView === 'staff' && (
                  <div style={{ position: 'relative', marginBottom: 10, maxWidth: 360 }}>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Staff member</label>
                    <input type="text" value={gtStaffQuery}
                      onChange={e => { setGtStaffQuery(e.target.value); setGtStaffMenuOpen(true); setGtForm(p => ({ ...p, staffId: null })); }}
                      onFocus={() => setGtStaffMenuOpen(true)}
                      onBlur={() => setTimeout(() => setGtStaffMenuOpen(false), 150)}
                      placeholder="Search staff by name..."
                      style={{ width: '100%', padding: '7px 9px', border: `1px solid ${gtForm.staffId ? '#A7F3D0' : '#D1D5DB'}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                    {gtForm.staffId && <span style={{ position: 'absolute', right: 10, top: 30, fontSize: 11, color: '#059669', fontWeight: 700 }}>✓</span>}
                    {gtStaffMenuOpen && gtStaffQuery && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, zIndex: 41, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                        {gtStaff
                          .filter(s => (s.Name || '').toLowerCase().includes(gtStaffQuery.toLowerCase()))
                          .slice(0, 50)
                          .map(s => (
                            <div key={s.StaffId}
                              onMouseDown={() => { setGtForm(p => ({ ...p, staffId: s.StaffId })); setGtStaffQuery(s.Name); setGtStaffMenuOpen(false); }}
                              style={{ padding: '7px 10px', fontSize: 13, color: '#374151', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                              <span>{s.Name}</span>
                              <span style={{ color: '#9CA3AF', fontSize: 11 }}>{s.DepartmentCode || ''} {s.Level || ''}</span>
                            </div>
                          ))}
                        {gtStaff.filter(s => (s.Name || '').toLowerCase().includes(gtStaffQuery.toLowerCase())).length === 0 && (
                          <div style={{ padding: '8px 10px', fontSize: 12, color: '#9CA3AF' }}>No match</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 100px', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Name</label>
                    <input type="text" value={gtForm.templateName}
                      onChange={e => setGtForm(p => ({ ...p, templateName: e.target.value }))}
                      placeholder="e.g., Full Time, PT 30, Busy Season Audit"
                      style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Department</label>
                    <select value={gtForm.departmentCode}
                      onChange={e => setGtForm(p => ({ ...p, departmentCode: e.target.value }))}
                      style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' }}>
                      <option value="">All depts</option>
                      {STAFF_DEPT_GROUPS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Priority</label>
                    <input type="number" min="0" max="99" value={gtForm.priority}
                      onChange={e => setGtForm(p => ({ ...p, priority: e.target.value }))}
                      style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Effective Start (optional)</label>
                    <input type="date" value={gtForm.effectiveStart}
                      onChange={e => setGtForm(p => ({ ...p, effectiveStart: e.target.value }))}
                      style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Effective End (optional)</label>
                    <input type="date" value={gtForm.effectiveEnd}
                      onChange={e => setGtForm(p => ({ ...p, effectiveEnd: e.target.value }))}
                      style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', cursor: 'pointer', marginBottom: 10 }}>
                  <input type="checkbox" checked={!!gtForm.recurringAnnually}
                    onChange={e => setGtForm(p => ({ ...p, recurringAnnually: e.target.checked }))} />
                  Recurs annually
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {gtForm.recurringAnnually
                      ? '— effective dates match by month/day every year (year ignored)'
                      : '— effective dates are exact one-time calendar dates'}
                  </span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 10 }}>
                  {[['monHours','Mon'],['tueHours','Tue'],['wedHours','Wed'],['thuHours','Thu'],['friHours','Fri'],['satHours','Sat'],['sunHours','Sun']].map(([k, label]) => (
                    <div key={k}>
                      <label style={{ display: 'block', fontSize: 10, color: '#6B7280', textAlign: 'center', marginBottom: 2 }}>{label}</label>
                      <input type="number" min="0" max="24" step="0.25" value={gtForm[k]}
                        onChange={e => setGtForm(p => ({ ...p, [k]: e.target.value }))}
                        style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, textAlign: 'center', fontWeight: 600, boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#374151', cursor: 'pointer' }}>
                    <input type="checkbox" checked={gtForm.isActive !== false}
                      onChange={e => setGtForm(p => ({ ...p, isActive: e.target.checked }))} />
                    Active
                  </label>
                  <div style={{ flex: 1 }} />
                  {gtEditingId && (
                    <button className="sched-admin-row-btn"
                      onClick={() => { setGtEditingId(null); setGtForm(blankGT(gtView)); setGtStaffQuery(''); }}>
                      Cancel
                    </button>
                  )}
                  {(() => {
                    const cantSave = !gtForm.templateName.trim() || gtSaving || (gtView === 'staff' && !gtForm.staffId);
                    return (
                      <button className="sched-admin-row-btn sched-admin-row-btn--primary"
                        disabled={cantSave}
                        onClick={handleSaveGT}
                        style={{ opacity: cantSave ? 0.5 : 1, cursor: cantSave ? 'not-allowed' : 'pointer' }}>
                        {gtSaving ? 'Saving...' : (gtEditingId ? 'Save Changes' : 'Add Template')}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* List */}
              {(() => {
                const showStaff = gtView === 'staff';
                const rows = (showStaff ? gtStaffList : gtList).filter(r => gtShowInactive || r.IsActive);
                const headers = showStaff
                  ? ['Staff','Name','Dept','Mon','Tue','Wed','Thu','Fri','Sat','Sun','Weekly','Priority','Effective','Active']
                  : ['Name','Dept','Mon','Tue','Wed','Thu','Fri','Sat','Sun','Weekly','Priority','Effective','Active'];
                const leftCols = ['Staff','Name','Dept','Effective'];
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 }}>{showStaff ? 'Staff-custom templates' : 'Global templates'}</div>
                      <label style={{ fontSize: 11, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 'auto' }}>
                        <input type="checkbox" checked={gtShowInactive} onChange={e => setGtShowInactive(e.target.checked)} />
                        Show inactive
                      </label>
                    </div>
                    {gtLoading ? (
                      <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading...</div>
                    ) : rows.length === 0 ? (
                      <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                        {showStaff ? 'No per-staff custom templates yet. Add one above to override a staffer’s schedule.' : 'No global templates yet. Add one above to get started.'}
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB' }}>
                            {headers.map((h) => (
                              <th key={h} style={{ padding: '8px 10px', textAlign: leftCols.includes(h) ? 'left' : 'center', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                            ))}
                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #E5E7EB', width: 130 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows
                            .sort((a, b) => showStaff
                              ? gtStaffName(a.StaffId).localeCompare(gtStaffName(b.StaffId)) || (a.TemplateName || '').localeCompare(b.TemplateName || '')
                              : (a.TemplateName || '').localeCompare(b.TemplateName || ''))
                            .map(row => {
                              const weekly = (row.MonHours || 0) + (row.TueHours || 0) + (row.WedHours || 0) + (row.ThuHours || 0) + (row.FriHours || 0) + (row.SatHours || 0) + (row.SunHours || 0);
                              const isEditing = gtEditingId === row.TemplateId;
                              return (
                                <tr key={row.TemplateId}
                                  className={isEditing ? 'sched-admin-table-row--editing' : ''}
                                  style={{ borderBottom: '1px solid #F3F4F6', opacity: row.IsActive ? 1 : 0.5 }}>
                                  {showStaff && <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{gtStaffName(row.StaffId)}</td>}
                                  <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>
                                    {row.TemplateName}
                                    {row.IsDefault ? <span title="Firm default — applied to staff with no assigned template" style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: BRAND.deepTeal, background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 4, padding: '1px 5px' }}>★ Default</span> : null}
                                  </td>
                                  <td style={{ padding: '8px 10px', color: '#6B7280' }}>{row.DepartmentCode || <span style={{ color: '#D1D5DB' }}>All</span>}</td>
                                  {['MonHours','TueHours','WedHours','ThuHours','FriHours','SatHours','SunHours'].map(k => (
                                    <td key={k} style={{ padding: '8px 10px', textAlign: 'center', color: row[k] > 0 ? '#374151' : '#D1D5DB' }}>{row[k]}</td>
                                  ))}
                                  <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: BRAND.deepTeal }}>{weekly}h</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{row.Priority || 0}</td>
                                  <td style={{ padding: '8px 10px', color: '#6B7280', fontSize: 11, whiteSpace: 'nowrap' }}>
                                    {fmtEffective(row)}
                                    {row.RecurringAnnually && <span title="Recurs annually" style={{ marginLeft: 5, color: BRAND.deepTeal, fontWeight: 700 }}>↻</span>}
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center', color: row.IsActive ? '#059669' : '#9CA3AF', fontWeight: 600, fontSize: 11 }}>{row.IsActive ? 'Yes' : 'No'}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    <button
                                      className="sched-admin-row-btn"
                                      onClick={() => handleEditGT(row)}
                                      style={isEditing ? { background: '#F59E0B', borderColor: '#F59E0B', color: '#fff' } : undefined}>
                                      {isEditing ? 'Editing' : 'Edit'}
                                    </button>
                                    <button
                                      className="sched-admin-row-btn sched-admin-row-btn--danger"
                                      onClick={() => handleDeleteGT(row)}>
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Delete-confirmation modal */}
            {gtDeleteConfirm && (
              <div className="sched-mgr-modal-backdrop" onClick={() => !gtDeleteBusy && setGtDeleteConfirm(null)}>
                <div className="sched-mgr-modal" style={{ width: 460, padding: 24 }} onClick={e => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#991B1B' }}>Delete Template</h3>
                  <p style={{ margin: '0 0 16px', fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                    Are you sure you want to delete <strong>{gtDeleteConfirm.TemplateName}</strong>?
                  </p>
                  <p style={{ margin: '0 0 20px', fontSize: 11, color: '#6B7280', lineHeight: 1.5 }}>
                    Any staff who use this as their default template will be unlinked &mdash; they'll fall back to dept-scoped globals.
                    Schedule entries are unaffected.
                  </p>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      className="sched-admin-row-btn"
                      disabled={gtDeleteBusy}
                      onClick={() => setGtDeleteConfirm(null)}>
                      Cancel
                    </button>
                    <button
                      className="sched-admin-row-btn sched-admin-row-btn--danger"
                      disabled={gtDeleteBusy}
                      style={{ background: '#DC2626', borderColor: '#DC2626', color: '#fff', opacity: gtDeleteBusy ? 0.6 : 1 }}
                      onClick={confirmDeleteGT}>
                      {gtDeleteBusy ? 'Deleting...' : 'Yes, delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ════ Closed Job Cleanup Tab ════ */}
        {/* No meStaffId: the sweep runs in 'manager' mode, which clears whole jobs rather
            than one person's rows, so it never needs the viewer's staff id. */}
        {adminTab === 'closed_jobs' && <ClosedJobsTab />}

        {/* ════ Sync & Data Tab ════ */}
        {adminTab === 'sync' && (
          <>
            {/* Sync Actions */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>Sync Actions</h3>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <button className="sched-assign-btn" onClick={handlePESync} disabled={syncing}
                  style={{ fontSize: 12 }}>
                  {syncing ? 'Syncing...' : '\u21BB Sync PE Data'}
                </button>
                {/* The Empire sync + upload buttons were removed 2026-08-20: BMSS no
                    longer syncs from Empire, and both wrote to rprs.ScheduleEntries
                    (including deletes), so leaving them clickable was a standing risk
                    for no benefit. The read-only Empire panels below stay \u2014 they
                    describe historical data, they don't write anything.
                    The api/empireSync Function still exists and is still reachable
                    directly; retiring it is a separate call. */}
              </div>
              {syncMsg && (
                <div style={{ fontSize: 12, padding: '8px 12px', background: syncMsg.includes('error') || syncMsg.includes('Error') ? '#FEF2F2' : '#D1FAE5',
                  borderRadius: 6, color: syncMsg.includes('error') || syncMsg.includes('Error') ? '#DC2626' : '#065F46', fontWeight: 600 }}>
                  {syncMsg}
                </div>
              )}
            </div>

            {/* Capacity drift vs Practice Engine (Trello #332).
                Capacity was seeded ONCE from PE on 2026-05-18 and never resynced, so
                drift accumulated silently for 99 days until users complained. This panel
                makes it visible, and the per-row Accept applies the same resolver the
                Phase-2 timer will use. */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>
                  Capacity vs Practice Engine
                </h3>
                {drift && (
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {drift.counts.actionable} actionable
                    {drift.counts.guarded ? ` · ${drift.counts.guarded} not yet applicable` : ''}
                    {' · '}{drift.counts.inSync} in sync of {drift.activeStaff}
                  </span>
                )}
                <div style={{ flex: 1 }} />
                <button className="sched-mgr-pill" style={{ fontSize: 11 }}
                  onClick={loadDrift} disabled={driftLoading}>
                  {driftLoading ? 'Checking…' : '↻ Re-check'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 12 }}>
                PE owns the baseline contract (Full Time / part-time / leave). Accepting writes
                to RPRS only — it does not change anything in PE. Busy-season templates are
                RPRS-native and are never touched here.
              </div>

              {driftMsg && (
                <div style={{ fontSize: 12, padding: '8px 12px', background: '#D1FAE5', borderRadius: 6,
                  color: '#065F46', fontWeight: 600, marginBottom: 10 }}>{driftMsg}</div>
              )}
              {driftErr && (
                <div style={{ fontSize: 12, padding: '8px 12px', background: '#FEF2F2', borderRadius: 6,
                  color: '#DC2626', fontWeight: 600, marginBottom: 10 }}>{driftErr}</div>
              )}

              {driftLoading && !drift ? (
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>Loading…</div>
              ) : !drift ? null : drift.rows.length === 0 ? (
                <div style={{ fontSize: 13, color: '#065F46', fontWeight: 600 }}>
                  ✓ All {drift.activeStaff} active staff match Practice Engine.
                </div>
              ) : (
                <table className="sched-admin-table" style={{ width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Staff</th>
                      <th style={{ textAlign: 'left' }}>Dept</th>
                      <th style={{ textAlign: 'left' }}>PE profile</th>
                      <th style={{ textAlign: 'left' }}>PE hours</th>
                      <th style={{ textAlign: 'left' }}>RPRS hours</th>
                      <th style={{ textAlign: 'right' }}>Δ/wk</th>
                      <th style={{ textAlign: 'left' }}>Resolves to</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {drift.rows.map(r => {
                      const delta = (r.rprsWeek ?? 0) - (r.peWeek ?? 0);
                      return (
                        <tr key={r.staffId} className={!r.actionable ? 'sched-admin-table-row--muted' : undefined}
                          style={!r.actionable ? { opacity: 0.6 } : undefined}>
                          <td style={{ fontWeight: 600 }}>
                            {r.name}
                            {r.ambiguousOverrides > 1 && (
                              <span title={`${r.ambiguousOverrides} equal-priority overrides — which one wins depends on row order`}
                                style={{ marginLeft: 5, color: '#B45309', fontWeight: 700 }}>⚠</span>
                            )}
                          </td>
                          <td style={{ color: '#6B7280' }}>{r.departmentCode || '—'}</td>
                          <td style={{ color: '#6B7280' }}>{r.peProfileDesc || '—'}</td>
                          <td style={{ fontFamily: 'monospace' }}>
                            {(r.peHours || []).join('/')} <span style={{ color: '#9CA3AF' }}>={r.peWeek}h</span>
                          </td>
                          <td style={{ fontFamily: 'monospace' }}>
                            {(r.rprsHours || []).join('/')} <span style={{ color: '#9CA3AF' }}>={r.rprsWeek}h</span>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700,
                            color: delta === 0 ? '#6B7280' : delta > 0 ? '#DC2626' : '#2563EB' }}>
                            {/* delta 0 with a row present = same weekly total, different
                                day split. That's Gaylon Hilyer's exact bug, so it must
                                not render as "no difference". */}
                            {delta === 0 ? 'shape' : `${delta > 0 ? '+' : ''}${delta}h`}
                          </td>
                          <td style={{ color: '#6B7280' }}>
                            {r.actionable
                              ? r.steps.map((s, i) => (
                                <div key={i}>
                                  {s.action === 'repoint-default' && `→ ${s.templateName}`}
                                  {s.action === 'deactivate-override' && `deactivate ${s.templateName}`}
                                  {s.action === 'upsert-override' && `${s.templateId ? 'update' : 'new'} override ${s.hours.join('/')}`}
                                </div>
                              ))
                              : <em>{r.reason}</em>}
                            {r.actionable && r.reason && (
                              <div style={{ color: '#B45309', fontSize: 11, marginTop: 2 }}>{r.reason}</div>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {r.actionable ? (
                              <button className="sched-admin-row-btn sched-admin-row-btn--primary"
                                style={{ fontSize: 11 }}
                                disabled={acceptingId === r.staffId}
                                onClick={() => handleAcceptPE(r)}>
                                {acceptingId === r.staffId ? 'Applying…' : 'Accept PE'}
                              </button>
                            ) : (
                              <span style={{ fontSize: 11, color: '#9CA3AF' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Last Synced Timestamps */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>Last Synced</h3>
              {syncLoading ? (
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>Loading...</div>
              ) : syncSummary ? (
                <div style={{ display: 'flex', gap: 24 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 2 }}>PE DATA</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{fmtTimestamp(syncSummary.timestamps?.PELastSync)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 2 }}>EMPIRE SCHEDULE</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{fmtTimestamp(syncSummary.timestamps?.EmpireLastSync)}</div>
                  </div>
                </div>
              ) : <div style={{ fontSize: 12, color: '#9CA3AF' }}>No data available</div>}
            </div>

            {/* Empire vs RPRS Coverage */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>Schedule Coverage (Current Week Onward)</h3>
              <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2, marginBottom: 16 }}>
                Compares entries sourced from Empire vs entries created natively in RPRS.
              </p>
              {syncLoading ? (
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>Loading...</div>
              ) : syncSummary?.coverage ? (() => {
                const c = syncSummary.coverage;
                const total = (c.EmpireEntries || 0) + (c.NativeEntries || 0);
                const empPct = total > 0 ? Math.round((c.EmpireEntries / total) * 100) : 0;
                const natPct = total > 0 ? Math.round((c.NativeEntries / total) * 100) : 0;
                return (
                  <div>
                    {/* Summary stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                      <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: BRAND.deepTeal }}>{total.toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>Total Entries</div>
                      </div>
                      <div style={{ background: '#FFFBEB', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#92400E' }}>{(c.EmpireEntries || 0).toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>Empire ({empPct}%)</div>
                      </div>
                      <div style={{ background: '#D1FAE5', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#065F46' }}>{(c.NativeEntries || 0).toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>RPRS Native ({natPct}%)</div>
                      </div>
                    </div>
                    {/* Staff counts */}
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#6B7280' }}>
                      <span>Empire staff: <strong>{c.EmpireStaffCount || 0}</strong></span>
                      <span>RPRS staff: <strong>{c.NativeStaffCount || 0}</strong></span>
                      {c.EmpireMinDate && <span>Empire range: {new Date(String(c.EmpireMinDate).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {'\u2013'} {new Date(String(c.EmpireMaxDate).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                    </div>
                  </div>
                );
              })() : <div style={{ fontSize: 12, color: '#9CA3AF' }}>No data available</div>}
            </div>

            {/* Per-Staff Comparison */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>Staff Hours: Empire vs RPRS</h3>
              <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2, marginBottom: 12 }}>
                Weekly totals per staff from current week onward. Shows staff with the most RPRS-native scheduling.
              </p>
              {syncLoading ? (
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>Loading...</div>
              ) : syncSummary?.staffComparison?.length > 0 ? (
                <div style={{ fontSize: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px', gap: 4, fontWeight: 700, color: '#374151', marginBottom: 4, paddingBottom: 4, borderBottom: '1px solid #E5E7EB' }}>
                    <span>Staff</span>
                    <span style={{ textAlign: 'right' }}>Empire</span>
                    <span style={{ textAlign: 'right' }}>RPRS</span>
                    <span style={{ textAlign: 'right' }}>Diff</span>
                  </div>
                  {syncSummary.staffComparison.map(s => {
                    const diff = (s.NativeHours || 0) - (s.EmpireHours || 0);
                    return (
                      <div key={s.StaffId} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px', gap: 4, padding: '3px 0', color: '#6B7280' }}>
                        <span style={{ color: '#374151', fontWeight: 500 }}>{s.StaffName}</span>
                        <span style={{ textAlign: 'right', color: '#92400E' }}>{Math.round((s.EmpireHours || 0) * 100) / 100}h</span>
                        <span style={{ textAlign: 'right', color: '#065F46' }}>{Math.round((s.NativeHours || 0) * 100) / 100}h</span>
                        <span style={{ textAlign: 'right', fontWeight: 600, color: diff > 0 ? '#065F46' : diff < 0 ? '#DC2626' : '#9CA3AF' }}>
                          {diff > 0 ? '+' : ''}{Math.round(diff * 100) / 100}h
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : <div style={{ fontSize: 12, color: '#9CA3AF' }}>No comparison data available</div>}
            </div>
          </>
        )}

        {/* ════ Bug Reports Tab (DEV_SUPERUSER) ════ */}
        {adminTab === 'bug_reports' && isDevSuperUser && <BugReportsTab />}

        {/* ════ Suggestions Tab (super-user OR DEV_SUPERUSER) ════ */}
        {adminTab === 'suggestions' && (isSuperUser || isDevSuperUser) && <SuggestionsTab />}

        {/* ════ Pool Audit Tab (§6 — super-user only, on demand) ════
            FIFO-bypass detection + any pool jobs whose PE write failed. The server
            re-checks super-user itself; this gate just keeps the tab out of sight. */}
        {adminTab === 'pool_audit' && isSuperUser && <PoolAuditTab />}

        </>
        )}
        </div>
      </div>
    </div>
  );
}
